import { useState, useCallback } from 'react';
import type { ServiceOffering, ServiceCapability } from '../../../types/backend';
import { useServiceOfferingsService } from '../services/serviceOfferingsService';
import type { ServiceCapabilityCreateRequest, ServiceCapabilityUpdateRequest } from '../services/serviceOfferingsService';

export function useCapabilityManagement(
  offeringId: string,
  onOfferingUpdate: (updated: ServiceOffering) => void
) {
  const service = useServiceOfferingsService();
  const [error, setError] = useState<string | null>(null);
  const [isWorking, setIsWorking] = useState(false);

  const add = useCallback(async (request: ServiceCapabilityCreateRequest): Promise<void> => {
    setIsWorking(true);
    setError(null);
    try {
      const updated = await service.addCapability(offeringId, request);
      onOfferingUpdate(updated);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to add capability';
      setError(msg);
      throw err;
    } finally {
      setIsWorking(false);
    }
  }, [service, offeringId, onOfferingUpdate]);

  const edit = useCallback(async (capabilityId: string, request: ServiceCapabilityUpdateRequest): Promise<void> => {
    setIsWorking(true);
    setError(null);
    try {
      const updated = await service.editCapabilityOverride(offeringId, capabilityId, request);
      onOfferingUpdate(updated);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to edit capability';
      setError(msg);
      throw err;
    } finally {
      setIsWorking(false);
    }
  }, [service, offeringId, onOfferingUpdate]);

  const remove = useCallback(async (capability: ServiceCapability): Promise<void> => {
    setIsWorking(true);
    setError(null);
    try {
      const updated = await service.removeCapability(offeringId, capability.capability_id);
      onOfferingUpdate(updated);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to remove capability';
      setError(msg);
      throw err;
    } finally {
      setIsWorking(false);
    }
  }, [service, offeringId, onOfferingUpdate]);

  return { add, edit, remove, error, isWorking };
}
